import { Component, OnInit } from '@angular/core';
import { LazyLoadEvent } from 'primeng/api';
import { VOCCourseModel } from 'src/app/core/VOCCourseModel';
import { AdminService } from 'src/app/services/admin/admin.service';

@Component({
  selector: 'app-admin-voc',
  templateUrl: './admin-voc.component.html',
  styleUrl: './admin-voc.component.scss'
})
export class AdminVOCComponent implements OnInit {

  vocCourses: VOCCourseModel[] = [];

  totalRecords = 0;

  pageNo = 1;

  pageSize = 10;

  firstRecord = 0;

  isLoading = false;

  isSavingJobNumber = false;

  showJobNumberDialog = false;

  selectedCourse: VOCCourseModel | null = null;

  jobNumberValue = '';

  courseFeeValue: number | null = null;

  successMessage = '';

  errorMessage = '';

  constructor(
    private adminService: AdminService
  ) {
  }

  ngOnInit(): void {
    this.getVOCCourses();
  }

  /**
   * Get the paginated VOC course form records.
   */
  getVOCCourses(): void {
    this.isLoading = true;
    this.errorMessage = '';

    this.adminService
      .getVOCCourses(
        'GET_ALL_ADMIN',
        this.pageNo,
        this.pageSize
      )
      .subscribe({
        next: response => {
          this.vocCourses =
            response?.records || [];

          this.totalRecords =
            response?.totalRecords || 0;

          this.isLoading = false;
        },
        error: error => {
          this.isLoading = false;

          this.errorMessage =
            error?.error?.message ||
            'Unable to load VOC course forms.';
        }
      });
  }

  /**
   * Handle PrimeNG server-side pagination.
   */
  onPageChange(event: LazyLoadEvent): void {
    const first =
      event.first ?? 0;

    const rows =
      event.rows ?? this.pageSize;

    this.firstRecord = first;
    this.pageSize = rows;

    this.pageNo =
      Math.floor(first / rows) + 1;

    this.getVOCCourses();
  }

  /**
   * Determine whether the course has a valid fee.
   */
  hasCourseFee(course: VOCCourseModel): boolean {
    return (
      course.courseFee !== null &&
      course.courseFee !== undefined
    );
  }

  /**
   * Open the add/edit details popup.
   */
  openJobNumberDialog(course: VOCCourseModel): void {
    this.selectedCourse = course;

    this.jobNumberValue =
      course.jobNumber || '';

    this.courseFeeValue =
      course.courseFee !== null &&
        course.courseFee !== undefined
        ? Number(course.courseFee)
        : null;

    this.successMessage = '';
    this.errorMessage = '';

    this.showJobNumberDialog = true;
  }

  /**
   * Close the popup and reset the values.
   */
  closeJobNumberDialog(): void {
    if (this.isSavingJobNumber) {
      return;
    }

    this.showJobNumberDialog = false;

    this.resetDialogValues();
  }

  /**
   * Reset popup values when the PrimeNG dialog closes.
   */
  onDialogHide(): void {
    if (!this.isSavingJobNumber) {
      this.resetDialogValues();
    }
  }

  /**
   * Reset the selected VOC course details.
   */
  private resetDialogValues(): void {
    this.selectedCourse = null;
    this.jobNumberValue = '';
    this.courseFeeValue = null;
    this.errorMessage = '';
  }

  /**
   * Save the job number and course fee.
   */
  saveJobNumber(): void {
    if (!this.selectedCourse) {
      this.errorMessage =
        'No VOC course request was selected.';

      return;
    }

    const jobNumber =
      this.jobNumberValue.trim();

    if (!jobNumber) {
      this.errorMessage =
        'Please enter a job number.';

      return;
    }

    if (
      this.courseFeeValue === null ||
      this.courseFeeValue === undefined ||
      Number.isNaN(Number(this.courseFeeValue)) ||
      Number(this.courseFeeValue) < 0
    ) {
      this.errorMessage =
        'Please enter a valid course fee.';

      return;
    }

    const courseFee =
      Number(this.courseFeeValue);

    this.isSavingJobNumber = true;
    this.successMessage = '';
    this.errorMessage = '';

    const request: VOCCourseModel = {
      ...this.selectedCourse,

      jobNumber: jobNumber,

      courseFee: courseFee,

      modifedDate:
        new Date().toISOString()
    };

    this.adminService
      .saveVocForm(
        request,
        'UPDATE_JOB_NUMBER'
      )
      .subscribe({
        next: response => {
          this.isSavingJobNumber = false;

          if (response?.isSuccess) {
            this.updateCourseInTable(
              request.id,
              jobNumber,
              courseFee,
              request.modifedDate
            );

            this.showJobNumberDialog = false;

            this.resetDialogValues();

            this.successMessage =
              response?.message ||
              'VOC course details updated successfully.';
          } else {
            this.errorMessage =
              response?.message ||
              'Unable to update the VOC course details.';
          }
        },
        error: error => {
          this.isSavingJobNumber = false;

          this.errorMessage =
            error?.error?.message ||
            'An error occurred while updating the VOC course details.';
        }
      });
  }

  /**
   * Update the edited record in the current table without reloading.
   */
  private updateCourseInTable(
    id: number,
    jobNumber: string,
    courseFee: number,
    modifiedDate?: string
  ): void {
    const courseIndex =
      this.vocCourses.findIndex(
        course => course.id === id
      );

    if (courseIndex === -1) {
      return;
    }

    this.vocCourses[courseIndex] = {
      ...this.vocCourses[courseIndex],

      jobNumber: jobNumber,

      courseFee: courseFee,

      modifedDate: modifiedDate
    };

    // Create a new array reference so Angular updates the table.
    this.vocCourses = [
      ...this.vocCourses
    ];
  }



  
  // =========================================================
  // Pagination
  // =========================================================

  // Total pages
  get totalPages(): number {

    return Math.max(
      1,
      Math.ceil(
        this.totalRecords / this.pageSize
      )
    );

  }


  // Page numbers
  get pageNumbers(): number[] {

    return Array.from(
      { length: this.totalPages },
      (_, index) => index + 1
    );

  }


  // First displayed record
  get startRecord(): number {

    if (this.totalRecords === 0) {
      return 0;
    }

    return this.firstRecord + 1;

  }


  // Last displayed record
  get endRecord(): number {

    return Math.min(
      this.firstRecord + this.vocCourses.length,
      this.totalRecords
    );

  }


  // Change current page
  changePage(page: number): void {

    if (
      !Number.isInteger(page) ||
      page < 1 ||
      page > this.totalPages ||
      this.isLoading
    ) {
      return;
    }

    this.pageNo = page;

    this.firstRecord =
      (this.pageNo - 1) * this.pageSize;

    this.getVOCCourses();

  }


  // Previous page
  previousPage(): void {

    this.changePage(this.pageNo - 1);

  }


  // Next page
  nextPage(): void {

    this.changePage(this.pageNo + 1);

  }


  // Rows per page change
  onPageSizeChange(): void {

    this.pageNo = 1;

    this.firstRecord = 0;

    this.getVOCCourses();

  }

}